/**
 * Schritt 3: Beispielhafte Simulation von Verkaufs-Szenarien
 * Ermöglicht die Simulation von Portfolio-Entnahmen mit FIFO-basierter Gewinnberechnung
 */

import React, { useState } from 'react';
import {
  Container,
  Header,
  Tabs,
  SpaceBetween,
  Alert,
  Box
} from '@cloudscape-design/components';

import { PortfolioPosition, EntnahmeSimulation, SteuerParameter } from '@/types';
import TabellenAnsichtTab from '@/components/TabellenAnsichtTab';
import VerkaufsanweisungenTab from '@/components/VerkaufsanweisungenTab';

interface Schritt3Props {
  portfolioPositionen: PortfolioPosition[];
  entnahmeSimulation: EntnahmeSimulation | null;
  hatVerkaufsEingaben: boolean;
  steuerParameter: SteuerParameter | null;
  onPortfolioUpdate: (positionen: PortfolioPosition[]) => void;
  onSimulationUpdate: (simulation: EntnahmeSimulation) => void;
  // Persistent form state managed at App level to prevent reset on navigation
  zielEntnahmebetrag: string;
  onZielEntnahmebetragUpdate: (betrag: string) => void;
  portfolioPercentageRaw: number;
  onPortfolioPercentageUpdate: (percentage: number) => void;
  sparerPauschbetragEnabled: boolean;
  onSparerPauschbetragUpdate: (enabled: boolean) => void;
}

/**
 * Hauptkomponente für Schritt 3 mit Tab-Navigation
 */
const Schritt3: React.FC<Schritt3Props> = ({
  portfolioPositionen,
  entnahmeSimulation,
  hatVerkaufsEingaben,
  steuerParameter,
  onPortfolioUpdate,
  onSimulationUpdate,
  zielEntnahmebetrag,
  onZielEntnahmebetragUpdate,
  portfolioPercentageRaw,
  onPortfolioPercentageUpdate,
  sparerPauschbetragEnabled,
  onSparerPauschbetragUpdate
}) => {
  const [activeTabId, setActiveTabId] = useState<string>('tabellen-ansicht');

  /**
   * Handler für Portfolio-Position-Updates
   */
  const handlePositionUpdate = (positionen: PortfolioPosition[]) => {
    onPortfolioUpdate(positionen);
  };

  /**
   * Tab-Konfiguration mit dynamischer Aktivierung
   */
  const tabs = [
    {
      id: 'tabellen-ansicht',
      label: 'Beispielhafte Simulation von Verkaufs-Szenarien',
      content: (
        <TabellenAnsichtTab
          portfolioPositionen={portfolioPositionen}
          onPositionUpdate={handlePositionUpdate}
          entnahmeSimulation={entnahmeSimulation}
          steuerParameter={steuerParameter}
          onSimulationUpdate={onSimulationUpdate}
          sparerPauschbetragEnabled={sparerPauschbetragEnabled}
          onSparerPauschbetragChange={onSparerPauschbetragUpdate}
          zielEntnahmebetrag={zielEntnahmebetrag}
          onZielEntnahmebetragChange={onZielEntnahmebetragUpdate}
          portfolioPercentageRaw={portfolioPercentageRaw}
          onPortfolioPercentageRawChange={onPortfolioPercentageUpdate}
        />
      )
    },
    {
      id: 'verkaufsanweisungen',
      label: 'Beispielhafte Verkaufsreihenfolge (Simulation)',
      disabled: !hatVerkaufsEingaben,
      content: (
        <VerkaufsanweisungenTab
          portfolioPositionen={portfolioPositionen.filter(p => !p.exclude && p.sellPercentage > 0)}
          entnahmeSimulation={entnahmeSimulation}
          steuerParameter={steuerParameter}
          sparerPauschbetragEnabled={sparerPauschbetragEnabled}
        />
      )
    }
  ];

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Diese Ansicht zeigt beispielhaft die steuerlichen Auswirkungen unterschiedlicher Verkaufs-Szenarien. Basierend auf vereinfachten Rechenregeln (FIFO, Teilfreistellung, Haltefristen). Keine steuerliche Beratung."
        >
          Beispielhafte Simulation von Verkaufs-Szenarien
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        {/* Wichtiger Hinweis */}
        <Alert type="warning" header="Wichtiger rechtlicher Hinweis">
          Diese Ansicht zeigt beispielhaft, wie sich unterschiedliche Verkaufs-Szenarien auf die steuerliche Auswirkung auswirken könnten. Die Berechnungen basieren auf vereinfachten gesetzlichen Standardregeln (FIFO, Teilfreistellung, Haltefristen) und dienen ausschließlich der allgemeinen Information. Keine Handlungsempfehlung, keine steuerliche Beratung.
        </Alert>

        {portfolioPositionen.length === 0 && (
          <Alert type="info">
            <strong>Keine Portfolio-Daten verfügbar</strong>
            <br />
            Bitte importieren Sie zunächst Transaktionsdaten in Schritt 2, um Portfolio-Positionen zu erstellen.
          </Alert>
        )}

        {portfolioPositionen.length > 0 && (
          <>
            {/* Tab-Navigation */}
            <Tabs
              activeTabId={activeTabId}
              onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
              tabs={tabs}
            />
          </>
        )}
      </SpaceBetween>
      
      {/* Footer-Disclaimer */}
      <Container>
        <Alert 
          type="warning" 
          dismissible={false}
          header="Wichtiger rechtlicher Hinweis:"
        >
          <SpaceBetween direction="vertical" size="s">
            <Box fontSize="body-m">
              Diese Ergebnisse sind rein hypothetisch und basieren auf vereinfachten Rechenmodellen sowie gesetzlichen Standardannahmen.
            </Box>
            <Box fontSize="body-m">
              Sie sind nicht auf Ihre individuelle Situation zugeschnitten und ersetzen keine steuerliche Beratung.
            </Box>
            <Box fontSize="body-m">
              Für verbindliche Entscheidungen wenden Sie sich bitte an eine Steuerberaterin oder einen Steuerberater.
            </Box>
          </SpaceBetween>
        </Alert>
      </Container>
    </Container>
  );
};

export default Schritt3;